import * as models from 'smg/customer-api/models';
import Konva from 'konva';
import {
    CenterLayer, CustomizerEngine, FitLayer, FlipLayerHorizontal, FlipLayerVertical,
    HtmlTransformer, ImageLayer, ResetLayer, RotateLayer, WireProcessedImageSync,
} from 'smg/customizer';
import { SetupClickAwayDeselect } from 'smg/customizer/click-away';
import * as toast from 'smg/toast';
import { DownloadURL } from 'smg/download';
import * as uploadClaims from 'smg/upload-claims';
import { Upload, UploadClaimFromURL, Uploader } from 'smg/uploader/v2';
import { UUID } from 'smg/uuid';
import { AiImageActionsContext, RemoveLayerBackground, UpscaleLayerImage } from '../product-customizer/ai-image-actions';
import { BuilderLayerList, LayerAction, LayerRow, LayerRowProvider } from '../product-customizer/layer-list';
import { PrintResolutionLayer, PrintResolutionWarning } from '../product-customizer/print-resolution-warning';
import { UploadPreviewURL } from '../product-customizer/upload-preview';
import { CanvasSize, ShapeClipFunc, WORKING_DPI } from './geometry';
import { InkShadow } from './shading-overlay';
import { PinTextDropUI } from './text-drop-ui';

/** Serializable snapshot of one upload, stored in the design's persisted state */
export interface PinImageState {
    uploadID: number;
    /** Permanent CDN URL (from the keep endpoint) */
    url: string;
    filename: string;
    position: { x: number; y: number };
    scale: { x: number; y: number };
    rotation: number;
    /** Canvas px at save time — restore rescales proportionally if the canvas differs */
    canvas?: { width: number; height: number };
    zIndex?: number;
    visible: boolean;
}

interface uploadedImage {
    layerID: string;
    uploadID: number;
    url: string;
    /** Original filename from upload */
    filename: string;
    /** Rasterized preview URL for vector files (PDF, AI, EPS, PSD) */
    previewUrl?: string;
    /** Permanent CDN URL (set after the keep endpoint succeeds) — blob URLs die on reload */
    permanentUrl?: string;
    /** S3 key proof for keep-by-proof-of-key */
    claim?: string;
    /** Transform + visibility carried across engine rebuilds (shape/size switches) */
    position?: { x: number; y: number };
    scale?: { x: number; y: number };
    rotation?: number;
    /** Canvas px at capture time — transforms rescale proportionally when the new
     * shape/size has a different canvas, so art keeps its place relative to the pin */
    canvas?: { width: number; height: number };
    /** Stacking position at capture time, so list reorders survive engine rebuilds */
    zIndex?: number;
    visible: boolean;
    /** Pre-AI-processing originals, stashed/restored by WireProcessedImageSync */
    originalUrl?: string;
    originalUploadID?: number;
    preUpscaleUrl?: string;
    preUpscaleUploadID?: number;
}

/**
 * Image uploads for the pin builder — the single-side counterpart to the coin builder's
 * ImageUploadUI. Owns the uploaded-image state, selection (HtmlTransformer handles), and
 * the design-tab layer list. The page recreates the engine on every shape/size switch, so
 * this class holds images by value and re-adds them via attachTo(); layers clip to the
 * current shape's printable area (ShapeClipFunc) instead of the coin's alpha masks.
 */
export class PinImageUploadUI {
    #engine: CustomizerEngine | null = null;
    #currentSize: models.LineItemTypeSize | null = null;
    #transformer: HtmlTransformer | null = null;
    #images = new Map<string, uploadedImage>();
    #selectedLayerId: string | null = null;
    #uploader: Uploader;
    #list: BuilderLayerList | null = null;
    #inFlight = 0;
    #processingCount = 0; // Tracks AI upscale/background removal and similar operations

    get #isProcessing(): boolean {
        return this.#inFlight > 0 || this.#processingCount > 0;
    }

    /** Whether an upload or AI process is running (page-level processing lock) */
    isProcessing(): boolean {
        return this.#isProcessing;
    }
    #$canvasOverlay: HTMLElement | null = null;
    #textDrop: PinTextDropUI | null = null;
    #lastKonvaDragEnd = 0;
    #printWarning: PrintResolutionWarning;

    /** Notifies the other selection owner (text drop) so selections stay exclusive */
    onLayerSelected?: (layerID: string) => void;

    /** The pin went from empty to designed or back (no uploads and no text blocks) */
    onEmptyChange?: (empty: boolean) => void;

    #$uploadZone: HTMLElement | null;
    #$fileInput: HTMLInputElement;
    #$uploadsSection: HTMLElement | null;
    #$layerList: HTMLElement | null;
    // The empty pin's own upload target (and its Upload · Add Text row)
    #$starter: HTMLElement | null;
    #$starterUploads: HTMLElement[];
    #$cornerBtn: HTMLElement | null;
    #$cornerAddText: HTMLElement | null;
    #$cornerBtnMobile: HTMLElement | null;
    #$stage: HTMLElement | null;

    constructor() {
        this.#$uploadZone = document.getElementById('pin-upload-zone');
        this.#$uploadsSection = document.getElementById('pin-design-uploads');
        this.#$layerList = document.getElementById('pin-design-layer-list');
        this.#$starter = document.getElementById('pin-starter');
        this.#$starterUploads = [...document.querySelectorAll<HTMLElement>('[data-pin-upload]')];
        this.#$cornerBtn = document.getElementById('pin-upload-corner-btn');
        this.#$cornerAddText = document.getElementById('pin-add-text-corner-btn');
        this.#$cornerBtnMobile = document.getElementById('pin-upload-corner-btn-mobile');
        this.#$stage = document.getElementById('coin-preview-container');

        // Hidden file input everything clicks through to — the zone, the canvas upload
        // buttons (no tab to reveal first, unlike coins — pins' card has no tabs), and
        // the list's "Add image" button
        this.#$fileInput = document.createElement('input');
        this.#$fileInput.type = 'file';
        // The accept list is rendered onto the zone from the builder's spec sheet
        // (data-upload-accept), so the page and this input never disagree.
        const accept = this.#$uploadZone?.dataset.uploadAccept;
        if (accept) {
            this.#$fileInput.accept = accept;
        }
        this.#$fileInput.multiple = true;
        this.#$fileInput.hidden = true;
        document.body.appendChild(this.#$fileInput);

        const openPicker = (): void => {
            // The click a pinch's release can leave on the empty pin isn't a request to upload
            if (this.#engine?.inViewGesture()) {
                return;
            }
            this.#$fileInput.click();
        };
        this.#$uploadZone?.addEventListener('click', openPicker);
        this.#$starterUploads.forEach($btn => $btn.addEventListener('click', openPicker));
        this.#$cornerBtn?.addEventListener('click', openPicker);
        this.#$cornerBtnMobile?.addEventListener('click', openPicker);

        // The canvas is `inches × WORKING_DPI` px at every shape/size, so a size switch
        // that grows the art on the pin lowers its print resolution and re-warns
        this.#printWarning = new PrintResolutionWarning({
            mount: () => document.getElementById('print-resolution-alerts'),
            product: 'pin',
            canvasDPI: () => WORKING_DPI,
            layers: () => this.#imageLayers(),
            upscale: layerID => {
                if (!this.#isProcessing) {
                    void UpscaleLayerImage(this.#imageActionsCtx(), layerID);
                }
            },
            onChange: () => this.#renderLayerList(),
        });

        this.#uploader = new Uploader({
            $input: this.#$fileInput,
            // The zone hides once uploads exist — the layer list takes over as the drop
            // target so drag-and-drop keeps working with layers present. A file dropped
            // anywhere on the stage lands too.
            $dropzones: [this.#$uploadZone, this.#$layerList, this.#$stage].filter((
                $el): $el is HTMLElement => $el !== null),
            // Block every intake path (picker, drop, paste) while a process runs
            disabled: () => this.#isProcessing,
            onStart: () => {
                this.#inFlight++;
                this.#setInteractionEnabled(false);
                this.#showLoadingState();
            },
            onComplete: (upload: Upload) => {
                void this.#handleUploadComplete(upload).finally(() => this.#uploadSettled());
            },
            onError: (_, err) => {
                this.#uploadSettled();
                toast.Error('Failed to upload image');
                console.error('[PinImageUploadUI] Upload error:', err);
            },
            onDragStart: () => {
                this.#$uploadZone?.classList.add('drag-over');
                this.#$layerList?.classList.add('drag-over');
                this.#$stage?.classList.add('drag-over');
            },
            onDragStop: () => {
                this.#$uploadZone?.classList.remove('drag-over');
                this.#$layerList?.classList.remove('drag-over');
                this.#$stage?.classList.remove('drag-over');
            },
        });

        // The selection's handles and bend control live on <body>, laid over the stage:
        // while a file is dragged they let it through, so a drop on selected artwork
        // still lands on the stage
        document.addEventListener('dragenter', e => {
            if (e.dataTransfer?.types.includes('Files')) {
                document.body.classList.add('pin-file-drag');
            }
        });
        const endFileDrag = (): void => document.body.classList.remove('pin-file-drag');
        document.addEventListener('drop', endFileDrag, true);
        document.addEventListener('dragend', endFileDrag);
        document.addEventListener('dragleave', e => {
            // Left the window
            if (e.relatedTarget === null) {
                endFileDrag();
            }
        });

        // The uploader only clears its drag highlight on a drop — a file dragged across
        // the stage and back out would leave the stage lit
        this.#$stage?.addEventListener('dragleave', e => {
            if (!(e.relatedTarget instanceof Node) || !this.#$stage?.contains(e.relatedTarget)) {
                this.#$stage?.classList.remove('drag-over');
            }
        });

        // Clicking outside the canvas deselects — the stage listener (attachTo) only
        // sees clicks that land on the canvas
        SetupClickAwayDeselect({
            protectedSelectors:
                '#coin-preview-container, .html-transformer-overlay, ' +
                '#pin-design-uploads, .design-layer-row, .layer-action-bar, ' +
                '#pin-upload-zone, #pin-starter, #pin-upload-corner-btn, #pin-upload-corner-btn-mobile',
            hasSelection: () => this.#selectedLayerId !== null,
            // 100ms matches DRAG_END_DEBOUNCE_MS in HtmlTransformer
            justFinishedDragging: () => (this.#transformer?.justFinishedDragging() ?? false) ||
                Date.now() - this.#lastKonvaDragEnd < 100,
            deselect: () => this.#deselect(),
        });

        if (this.#$layerList) {
            this.#list = new BuilderLayerList({
                containers: [this.#$layerList],
                providers: [this.#imageProvider(), this.#textProvider()],
                reorder: orderedIds => {
                    if (this.#isProcessing) {
                        this.#renderLayerList();
                        return;
                    }
                    // Top = front, bottom = back: process bottom-up, bringing each to
                    // front so the last one processed ends up on top
                    for (let i = orderedIds.length - 1; i >= 0; i--) {
                        this.#engine?.bringLayerToFront(orderedIds[i]);
                    }
                    this.#engine?.emit('significantChange', {});
                    this.#renderLayerList();
                },
                onEmptyStateChange: empty => this.#applyEmptyState(empty),
                onAddImage: () => this.#$fileInput.click(),
                onAddText: () => {
                    void this.#textDrop?.addBlock();
                },
                labelElements: () => [this.#$uploadsSection?.querySelector('.design-uploads-label') ?? null],
                // Re-renders clear innerHTML — keep the spinner while a batch is in flight
                extraContent: () => {
                    if (this.#inFlight <= 0) {
                        return null;
                    }
                    const $spinner = document.createElement('div');
                    $spinner.className = 'upload-spinner';
                    $spinner.innerHTML = '<div class="spinner-border text-secondary" role="status"><span class="visually-hidden">Uploading...</span></div>';
                    return $spinner;
                },
                // The zone↔list swap changes the card's height — the page re-equalizes
                changedEventName: 'pin-uploads-changed',
            });
        }
    }

    #showLoadingState(): void {
        // Canvas overlay spinner (the coins page's coin-loading-overlay)
        const $canvasContainer = document.getElementById('coin-preview-container');
        if ($canvasContainer && !this.#$canvasOverlay) {
            this.#$canvasOverlay = document.createElement('div');
            this.#$canvasOverlay.className = 'coin-loading-overlay';
            this.#$canvasOverlay.innerHTML = `
                <div class="d-flex flex-column align-items-center gap-2">
                    <div class="spinner-border text-secondary" role="status">
                        <span class="visually-hidden">Uploading...</span>
                    </div>
                    <span class="fw-600 fs-7">Uploading...</span>
                </div>`;
            $canvasContainer.appendChild(this.#$canvasOverlay);
        }

        // Spinner in whichever upload container is showing (zone before first upload,
        // layer list after)
        for (const $container of [this.#$uploadZone, this.#$layerList]) {
            if (!$container?.offsetParent) {
                continue;
            }
            $container.classList.add('uploading');
            if (!$container.querySelector('.upload-spinner')) {
                const $spinner = document.createElement('div');
                $spinner.className = 'upload-spinner';
                $spinner.innerHTML = '<div class="spinner-border text-secondary" role="status"><span class="visually-hidden">Uploading...</span></div>';
                $container.appendChild($spinner);
            }
        }
    }

    #uploadSettled(): void {
        this.#inFlight = Math.max(0, this.#inFlight - 1);
        if (this.#inFlight > 0) {
            return;
        }
        this.#$canvasOverlay?.remove();
        this.#$canvasOverlay = null;
        for (const $container of [this.#$uploadZone, this.#$layerList]) {
            $container?.classList.remove('uploading');
            $container?.querySelector('.upload-spinner')?.remove();
        }
        if (!this.#isProcessing) {
            this.#setInteractionEnabled(true);
        }
    }

    /**
     * Lock/unlock the whole editing surface around an async process (upload,
     * AI upscale/background removal). The engine is recreated on every
     * shape/size switch, so a switch mid-process would strand the result on a
     * dead engine — and mid-process edits race the result generally (#6553).
     */
    #setInteractionEnabled(enabled: boolean): void {
        // Blanket lock: inert blocks clicks, focus, and keyboard for the whole
        // builder subtree (shape/size selectors included); the targeted
        // disables below add visual affordance and a fallback
        document.getElementById('main')?.toggleAttribute('inert', !enabled);

        this.#$fileInput.disabled = !enabled;

        for (const $el of [
            this.#$uploadZone,
            this.#$uploadsSection,
            this.#$layerList,
            this.#$starter,
            this.#$cornerBtn,
            this.#$cornerBtnMobile,
        ]) {
            $el?.classList.toggle('processing-locked', !enabled);
        }

        this.#engine?.getStage().listening(enabled);
        this.#transformer?.setEnabled(enabled);

        if (!enabled) {
            this.#list?.closeActionBar();
        }
    }

    /**
     * Bind to a freshly-created engine after a shape/size switch: recreates the
     * transformer, re-adds every stored image with its saved transform, and re-applies
     * the printable-area clip for the new shape.
     */
    async attachTo(engine: CustomizerEngine, sizeInit: models.ILineItemTypeSize): Promise<void> {
        const size = new models.LineItemTypeSize(sizeInit);
        this.#transformer?.detach();
        this.#engine = engine;
        this.#currentSize = size;
        this.#transformer = new HtmlTransformer(engine, {}, { rotationSnap: true });
        this.#transformer.hide();

        // AI bg-removal/upscale (and their undos) repoint the upload record at the
        // processed file so duplicate, delete-undo, download, and persistence follow
        WireProcessedImageSync(engine, layerID => this.#images.get(layerID));
        this.#printWarning.watch(engine);

        // Clicking empty canvas (or the blank) deselects
        engine.getStage().on('click tap', e => {
            // The release of a pinch isn't a tap on the artwork
            if (engine.inViewGesture()) {
                return;
            }
            // A click that Konva targeted at a text node belongs to PinTextDropUI — the
            // text is topmost at that point, so don't steal the selection for an image
            if (e.target instanceof Konva.Text || e.target instanceof Konva.TextPath) {
                return;
            }
            const stage = e.currentTarget.getStage();
            const pointer = stage?.getPointerPosition();
            if (pointer) {
                const topLayer = engine.getTopmostImageLayerAt(pointer.x, pointer.y);
                if (topLayer) {
                    this.#selectLayer(topLayer);
                    return;
                }
            }
            this.#deselect();
        });

        // Ascending captured z-order: each add brings itself to front, so re-adding
        // bottom-first reconstructs the stacking the user arranged
        const ordered = [...this.#images.values()].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
        for (const image of ordered) {
            await this.#addLayerFor(image);
        }
        this.#renderLayerList();
    }

    /** Snapshot every layer's live transform into the map before the engine is torn down */
    captureTransforms(): void {
        if (!this.#engine) {
            return;
        }
        for (const image of this.#images.values()) {
            const layer = this.#engine.getLayer(image.layerID);
            if (!(layer instanceof ImageLayer)) {
                continue;
            }
            image.position = layer.getPosition();
            image.scale = layer.getScale();
            image.rotation = layer.getRotation();
            image.canvas = this.#currentSize ? CanvasSize(this.#currentSize) : undefined;
            image.zIndex = layer.getActualZIndex();
            image.visible = layer.isVisible();
        }
        this.#deselect();
    }

    hasUploads(): boolean {
        return this.#images.size > 0;
    }

    /** Upload IDs for the cart, excluding hidden layers (they don't print) */
    uploadIDs(): number[] {
        const ids: number[] = [];
        for (const image of this.#images.values()) {
            if (image.uploadID > 0 && image.visible) {
                ids.push(image.uploadID);
            }
        }
        return ids;
    }

    /**
     * Like uploadIDs(), but resolves only after every in-flight file is actually in S3
     * (the uploader gates on its outstanding PUTs) — use this at add-to-cart time.
     */
    async settledUploadIDs(): Promise<number[]> {
        await this.#uploader.uploadIDs();
        return this.uploadIDs();
    }

    deselectAll(): void {
        this.#deselect();
    }

    /** Upload IDs that still need a permanent URL from the keep endpoint */
    getUploadIDs(): number[] {
        const ids: number[] = [];
        for (const image of this.#images.values()) {
            if (image.uploadID > 0 && !image.permanentUrl) {
                ids.push(image.uploadID);
            }
        }
        return ids;
    }

    /** Cache permanent URLs after the keep endpoint succeeds (avoids re-keeping every save) */
    setPermanentUrls(urls: Map<number, string>): void {
        for (const image of this.#images.values()) {
            const permanentUrl = urls.get(image.uploadID);
            if (permanentUrl) {
                image.permanentUrl = permanentUrl;
                image.claim = UploadClaimFromURL(permanentUrl);
                uploadClaims.Register([{ uploadID: image.uploadID, claim: image.claim }]);
            }
        }
    }

    /** Serializable upload states for persistence — only uploads with a permanent URL survive a reload */
    getState(): PinImageState[] {
        const states: PinImageState[] = [];
        for (const image of this.#images.values()) {
            if (image.uploadID <= 0 || !image.permanentUrl) {
                continue;
            }
            const layer = this.#engine?.getLayer(image.layerID);
            const live = layer instanceof ImageLayer ? layer : null;
            states.push({
                uploadID: image.uploadID,
                url: image.permanentUrl,
                filename: image.filename,
                position: live?.getPosition() ?? image.position ?? { x: 0, y: 0 },
                scale: live?.getScale() ?? image.scale ?? { x: 1, y: 1 },
                rotation: live?.getRotation() ?? image.rotation ?? 0,
                canvas: this.#currentSize ? CanvasSize(this.#currentSize) : image.canvas,
                zIndex: live?.getActualZIndex() ?? image.zIndex,
                visible: live?.isVisible() ?? image.visible,
            });
        }
        return states;
    }

    /**
     * Seed the upload map from persisted state. Call BEFORE the initial attachTo() —
     * the page then renders the saved shape/size and attachTo re-adds these like any
     * engine rebuild (rescaling against each state's saved canvas).
     */
    restoreState(states: PinImageState[]): void {
        // Restore is whole-design: replace any existing uploads
        this.#deselect();
        for (const layerID of [...this.#images.keys()]) {
            this.#images.delete(layerID);
            this.#engine?.removeLayer(layerID);
        }

        for (const state of states) {
            if (!state.url) {
                continue;
            }
            // Vector originals (PDF/AI/EPS/PSD/TIFF) can't render in Konva directly —
            // ask image-transform for a rasterized PNG
            const ext = state.filename.toLowerCase().split('.').pop() ?? '';
            const needsRaster = !['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext);
            const url = needsRaster ? `${state.url}?format=png` : state.url;

            const layerID = `img-${UUID()}`;
            const restored: uploadedImage = {
                layerID,
                uploadID: state.uploadID,
                url,
                permanentUrl: state.url,
                filename: state.filename,
                position: state.position,
                scale: state.scale,
                rotation: state.rotation,
                canvas: state.canvas,
                zIndex: state.zIndex,
                visible: state.visible,
                claim: UploadClaimFromURL(state.url),
            };
            this.#images.set(layerID, restored);
            uploadClaims.Register([{ uploadID: restored.uploadID, claim: restored.claim }]);
        }
    }

    /** Wire in the text-drop owner: its blocks render as rows in the shared layer list */
    setTextDropUI(textDrop: PinTextDropUI): void {
        this.#textDrop = textDrop;
        document.addEventListener('pin-text-drop-changed', () => {
            this.#renderLayerList();
        });
    }

    async #handleUploadComplete(upload: Upload): Promise<void> {
        const engine = this.#engine;
        const size = this.#currentSize;
        if (!engine || !size || !upload.data) {
            return;
        }

        const filename = upload.name || `img${upload.id ?? 0}.jpg`;
        const blobURL = URL.createObjectURL(upload.data);
        const previewURL = await UploadPreviewURL(upload.id ?? 0, blobURL, filename);

        const image: uploadedImage = {
            layerID: `img-${UUID()}`,
            uploadID: upload.id ?? 0,
            url: blobURL,
            previewUrl: previewURL !== blobURL ? previewURL : undefined,
            filename,
            visible: true,
            claim: upload.tmpKey,
        };
        this.#images.set(image.layerID, image);
        uploadClaims.Register([{ uploadID: image.uploadID, claim: image.claim }]);

        try {
            await this.#addLayerFor(image);
            this.#selectLayer(image.layerID);
            this.#renderLayerList();
            toast.Success('Image uploaded successfully');
        } catch (error) {
            this.#images.delete(image.layerID);
            toast.Error('Failed to add image to canvas');
            console.error('[PinImageUploadUI] Add to canvas error:', error);
        }
    }

    /** Create the engine layer for a stored image — used by both upload and re-attach */
    async #addLayerFor(image: uploadedImage): Promise<void> {
        const engine = this.#engine;
        const size = this.#currentSize;
        if (!engine || !size) {
            return;
        }

        const imageLayer = new ImageLayer(image.layerID, {
            userEditable: true,
            resizable: true,
        });
        await imageLayer.setImage(image.previewUrl ?? image.url);
        await engine.addLayer(image.layerID, imageLayer);
        // Newest upload lands on top (and above the blank/background)
        engine.bringLayerToFront(image.layerID);

        // Ink is ink at print time: crop to the shape's printable area
        imageLayer.setClipFunc(ShapeClipFunc(size));

        const canvas = CanvasSize(size);
        if (image.position && image.scale) {
            // Re-attach after a shape/size switch: restore the saved transform, rescaled
            // proportionally when the canvas changed so art stays put relative to the pin
            const ratioX = image.canvas ? canvas.width / image.canvas.width : 1;
            const ratioY = image.canvas ? canvas.height / image.canvas.height : 1;
            imageLayer.setPosition(image.position.x * ratioX, image.position.y * ratioY);
            imageLayer.setScale(image.scale.x * ratioX, image.scale.y * ratioX);
            imageLayer.setRotation(image.rotation ?? 0);
        } else {
            // Fresh upload: fit the whole image inside the canvas, centered — these pins
            // print edge-to-edge, so art meets the metal edge and the shape clip crops
            // whatever the blank doesn't cover. Contain (not cover) keeps logos fully
            // visible and the transform box manageable; the user scales from there.
            const natural = imageLayer.getNaturalSize();
            if (natural.width > 0 && natural.height > 0) {
                const contain = Math.min(canvas.width / natural.width, canvas.height / natural.height);
                imageLayer.setScale(contain);
            }
            imageLayer.setPosition(canvas.width / 2, canvas.height / 2);
        }
        if (!image.visible) {
            imageLayer.setVisible(false);
        }

        imageLayer.on('click tap dragstart', () => {
            // A pinch's release (or a finger landing mid-pinch) isn't a tap on the artwork
            if (engine.inViewGesture()) {
                return;
            }
            this.#selectLayer(image.layerID);
        });
        // Timestamp for the document click-away handler's drag guard
        imageLayer.on('dragend', () => {
            this.#lastKonvaDragEnd = Date.now();
        });

        // Raised-print indent seam, drawn with the node so it tracks every move live
        const node = imageLayer.getImageNode();
        if (node) {
            InkShadow(node);
        }
        engine.batchDraw();
    }

    #selectLayer(layerID: string): void {
        const layer = this.#engine?.getLayer(layerID);
        if (!(layer instanceof ImageLayer)) {
            return;
        }
        // One image is selected at a time — the one before stops taking wheel resizes
        const previous = this.#selectedLayerId === layerID ? null : this.#selectedLayerId;
        const previousLayer = previous ? this.#engine?.getLayer(previous) : null;
        if (previousLayer instanceof ImageLayer) {
            previousLayer.deselect();
        }
        this.#selectedLayerId = layerID;
        layer.select();
        const node = layer.getImageNode();
        if (node) {
            this.#transformer?.attachTo(node);
        }
        this.onLayerSelected?.(layerID);
        this.#renderLayerList();
    }

    #deselect(): void {
        if (this.#selectedLayerId) {
            const layer = this.#engine?.getLayer(this.#selectedLayerId);
            if (layer instanceof ImageLayer) {
                layer.deselect();
            }
        }
        this.#selectedLayerId = null;
        this.#transformer?.detach();
        this.#renderLayerList();
    }

    getSelectedLayerId(): string | null {
        return this.#selectedLayerId;
    }

    /** The selected upload's Konva node (keyboard nudge etc.) */
    getSelectedNode(): Konva.Node | null {
        if (!this.#selectedLayerId) {
            return null;
        }
        const layer = this.#engine?.getLayer(this.#selectedLayerId);
        return layer instanceof ImageLayer ? layer.getImageNode() ?? null : null;
    }

    /**
     * Delete with an undo toast + engine undo action (the text blocks' pattern).
     * The blob URL is deliberately NOT revoked — the undo path needs it alive.
     */
    deleteImageWithUndo(layerID: string): void {
        // Keyboard delete bypasses the pointer lock — a layer mutation
        // mid-process would strand the result on a deleted layer
        if (this.#isProcessing) {
            return;
        }
        const image = this.#images.get(layerID);
        const engine = this.#engine;
        if (!image || !engine) {
            return;
        }
        // Freshen the stored transform so undo restores the exact placement
        const layer = engine.getLayer(layerID);
        if (layer instanceof ImageLayer) {
            image.position = layer.getPosition();
            image.scale = layer.getScale();
            image.rotation = layer.getRotation();
            image.canvas = this.#currentSize ? CanvasSize(this.#currentSize) : image.canvas;
            image.zIndex = layer.getActualZIndex();
            image.visible = layer.isVisible();
        }

        this.#removeImage(layerID);

        // Instant action, undo via toast — no confirm dialog (see the undo-pattern skill).
        // Shares the engine undo stack with Ctrl+Z — one record of the deletion
        toast.Undo('Image removed', () => {
            this.#engine?.undoLocal();
        });

        engine.addAction({
            type: 'DELETE_IMAGE',
            layerId: layerID,
            description: `Delete image "${image.filename}"`,
            undo: () => {
                void this.#restoreImage(image);
            },
            redo: () => {
                this.#removeImage(layerID);
            },
        });
    }

    #removeImage(layerID: string): void {
        if (this.#selectedLayerId === layerID) {
            this.#deselect();
        }
        this.#images.delete(layerID);
        this.#engine?.removeLayer(layerID);
        this.#renderLayerList();
    }

    /** Rebuild a deleted upload from its stored record (the undo path) */
    async #restoreImage(image: uploadedImage): Promise<void> {
        this.#images.set(image.layerID, image);
        await this.#addLayerFor(image);
        this.#renderLayerList();
    }

    #toggleVisibility(layerID: string): void {
        const image = this.#images.get(layerID);
        const layer = this.#engine?.getLayer(layerID);
        if (!image || !layer) {
            return;
        }
        image.visible = !layer.isVisible();
        layer.setVisible(image.visible);
        if (!image.visible && this.#selectedLayerId === layerID) {
            this.#deselect();
            return; // #deselect re-renders
        }
        this.#renderLayerList();
    }

    #renderLayerList(): void {
        this.#list?.render();
    }

    /** Empty↔filled chrome: upload zone + canvas CTAs vs the layer-list section */
    #applyEmptyState(empty: boolean): void {
        this.#$uploadsSection?.classList.toggle('d-none', empty);
        this.#$uploadZone?.classList.toggle('hidden', !empty);
        this.#$starter?.classList.toggle('d-none', !empty);
        // The pin's own Upload · Add Text row stands in for the corner pair while empty
        this.#$cornerBtn?.classList.toggle('d-none', empty);
        this.#$cornerAddText?.classList.toggle('d-none', empty);
        // A phone's action row always carries Upload, filled while it's the next step
        this.#$cornerBtnMobile?.classList.toggle('btn-primary', empty);
        this.#$cornerBtnMobile?.classList.toggle('btn-outline-primary', !empty);
        this.onEmptyChange?.(empty);
        if (empty && this.#$layerList) {
            this.#$layerList.innerHTML = '';
        }
    }

    #imageProvider(): LayerRowProvider {
        return {
            rows: (): LayerRow[] => [...this.#images.values()].map(image => ({
                id: image.layerID,
                name: image.filename,
                thumbUrl: image.previewUrl ?? image.url,
                visible: image.visible,
                selected: this.#selectedLayerId === image.layerID,
                z: this.#engine?.getLayer(image.layerID)?.getActualZIndex() ?? 0,
                warning: this.#printWarning.badge(image.layerID),
            })),
            has: id => this.#images.has(id),
            select: id => this.#selectLayer(id),
            toggleVisibility: id => this.#toggleVisibility(id),
            actions: id => this.#imageActions(id),
        };
    }

    #textProvider(): LayerRowProvider {
        return {
            rows: (): LayerRow[] => (this.#textDrop?.blocks() ?? []).map(({ id, layer }) => ({
                id,
                name: layer.getText().trim() || 'Text',
                glyphIcon: 'fa-font',
                visible: layer.isVisible(),
                selected: this.#textDrop?.getSelectedId() === id,
                z: layer.getActualZIndex(),
            })),
            has: id => this.#textDrop?.hasBlock(id) ?? false,
            select: id => this.#textDrop?.select(id),
            toggleVisibility: id => this.#textDrop?.toggleVisibility(id),
            actions: id => this.#textActions(id),
        };
    }

    /** Every upload's live engine layer, for the print-resolution check */
    #imageLayers(): PrintResolutionLayer[] {
        const layers: PrintResolutionLayer[] = [];
        for (const image of this.#images.values()) {
            const layer = this.#engine?.getLayer(image.layerID);
            if (layer instanceof ImageLayer) {
                layers.push({ id: image.layerID, filename: image.filename, layer });
            }
        }
        return layers;
    }

    #imageActionsCtx(): AiImageActionsContext {
        return {
            engine: () => this.#engine ?? undefined,
            center: () => {
                const canvas = this.#currentSize ? CanvasSize(this.#currentSize) : { width: 0, height: 0 };
                return { x: canvas.width / 2, y: canvas.height / 2 };
            },
            // Contain, matching fresh-upload placement: whole image visible inside the
            // canvas; the shape clip crops whatever the blank doesn't cover
            fitScale: layer => {
                const canvas = this.#currentSize ? CanvasSize(this.#currentSize) : null;
                const natural = layer.getNaturalSize();
                if (!canvas || natural.width === 0 || natural.height === 0) {
                    return 1;
                }
                return Math.min(canvas.width / natural.width, canvas.height / natural.height);
            },
            canvasContainer: () => document.getElementById('coin-preview-container'),
            // AI processing must finish before the engine can be swapped out by
            // a shape/size switch — lock the whole surface while it runs
            setBusy: busy => {
                if (busy) {
                    this.#processingCount++;
                    this.#setInteractionEnabled(false);
                } else {
                    this.#processingCount--;
                    if (!this.#isProcessing) {
                        this.#setInteractionEnabled(true);
                    }
                }
            },
        };
    }

    /** Action-bar runs no-op while the processing lock is on (mirrors the CSS pointer lock) */
    #guardedActions(actions: LayerAction[]): LayerAction[] {
        return actions.map(action => ({
            ...action,
            run: () => {
                if (!this.#isProcessing) {
                    action.run();
                }
            },
        }));
    }

    #imageActions(layerID: string): LayerAction[] {
        const ctx = this.#imageActionsCtx();
        const layer = this.#engine?.getLayer(layerID);
        // Once upscaled, drop the action — upscaling again is a no-op
        const upscaled = layer instanceof ImageLayer && layer.hasUpscaled();
        return this.#guardedActions([
            { icon: 'fa-redo-alt', lines: ['Rotate', 'Right'], run: () => RotateLayer(ctx, layerID, 90) },
            { icon: 'fa-undo-alt', lines: ['Rotate', 'Left'], run: () => RotateLayer(ctx, layerID, -90) },
            { icon: 'fa-arrows-alt-h', lines: ['Flip', 'Horiz.'], run: () => FlipLayerHorizontal(ctx, layerID) },
            { icon: 'fa-arrows-alt-v', lines: ['Flip', 'Vertical'], run: () => FlipLayerVertical(ctx, layerID) },
            { icon: 'fa-angle-double-up', lines: ['Bring to', 'Front'], run: () => this.#bringToFront(layerID) },
            { icon: 'fa-angle-double-down', lines: ['Send to', 'Back'], run: () => this.#sendToBack(layerID) },
            { icon: 'fa-expand', lines: ['Fit to', 'Screen'], run: () => FitLayer(ctx, layerID) },
            { icon: 'fa-crosshairs', lines: ['Center', 'Image'], run: () => CenterLayer(ctx, layerID) },
            { icon: 'fa-trash-alt', lines: ['Delete', 'Image'], run: () => this.deleteImageWithUndo(layerID) },
            {
                icon: 'fa-clone',
                lines: ['Duplicate', 'Image'],
                run: () => {
                    void this.#duplicateImage(layerID);
                },
            },
            {
                icon: 'fa-magic',
                lines: ['AI Background', 'Removal'],
                run: () => {
                    void RemoveLayerBackground(ctx, layerID);
                },
            },
            ...upscaled ? [] : [{
                icon: 'fa-search-plus',
                lines: ['AI Upscale', 'Image'] as [string, string],
                run: () => {
                    void UpscaleLayerImage(ctx, layerID);
                },
            }],
            { icon: 'fa-file-import', lines: ['Replace', 'Image'], run: () => this.#$fileInput.click() },
            {
                icon: 'fa-download',
                lines: ['Download', 'Image'],
                run: () => {
                    void this.#downloadImage(layerID);
                },
            },
            { icon: 'fa-sync-alt', lines: ['Reset', 'Transform'], run: () => ResetLayer(ctx, layerID) },
        ]);
    }

    #textActions(id: string): LayerAction[] {
        return this.#guardedActions([
            { icon: 'fa-arrows-alt-h', lines: ['Flip', 'Horiz.'], run: () => this.#textDrop?.flipBlock(id, 'x') },
            { icon: 'fa-arrows-alt-v', lines: ['Flip', 'Vertical'], run: () => this.#textDrop?.flipBlock(id, 'y') },
            { icon: 'fa-angle-double-up', lines: ['Bring to', 'Front'], run: () => this.#bringToFront(id) },
            { icon: 'fa-angle-double-down', lines: ['Send to', 'Back'], run: () => this.#sendToBack(id) },
            { icon: 'fa-crosshairs', lines: ['Center', 'Text'], run: () => this.#textDrop?.centerBlock(id) },
            { icon: 'fa-trash-alt', lines: ['Delete', 'Text'], run: () => this.#textDrop?.deleteBlock(id) },
            {
                icon: 'fa-clone',
                lines: ['Duplicate', 'Text'],
                run: () => {
                    void this.#textDrop?.duplicateBlock(id);
                },
            },
            { icon: 'fa-sync-alt', lines: ['Reset', 'Transform'], run: () => this.#textDrop?.resetBlockTransform(id) },
        ]);
    }

    #bringToFront(layerID: string): void {
        this.#engine?.bringLayerToFront(layerID);
        this.#engine?.emit('significantChange', { layerId: layerID });
        this.#renderLayerList();
    }

    #sendToBack(layerID: string): void {
        this.#engine?.sendToBackOfUserLayers(layerID);
        this.#engine?.emit('significantChange', { layerId: layerID });
        this.#renderLayerList();
    }

    /** Clone an upload's record + layer, offset slightly so both copies stay visible */
    async #duplicateImage(sourceLayerID: string): Promise<void> {
        const source = this.#images.get(sourceLayerID);
        const sourceLayer = this.#engine?.getLayer(sourceLayerID);
        if (!source || !(sourceLayer instanceof ImageLayer)) {
            return;
        }
        const position = sourceLayer.getPosition();
        const copy: uploadedImage = {
            ...source,
            layerID: `img-${UUID()}`,
            position: { x: position.x + 30, y: position.y + 30 },
            scale: sourceLayer.getScale(),
            rotation: sourceLayer.getRotation(),
            canvas: this.#currentSize ? CanvasSize(this.#currentSize) : source.canvas,
            zIndex: undefined,
            visible: true,
        };
        this.#images.set(copy.layerID, copy);
        await this.#addLayerFor(copy);
        this.#renderLayerList();
        toast.Success('Image duplicated');
    }

    async #downloadImage(layerID: string): Promise<void> {
        const image = this.#images.get(layerID);
        if (!image) {
            return;
        }
        await DownloadURL(image.filename, { url: image.permanentUrl ?? image.url });
    }
}
